import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface PlantRiskAssessmentChartProps {
  data: Array<{
    plant_name: string;
    overdue_workshops: number;
    low_progress: number;
    high_risk: number;
    delayed_deadlines: number;
    resource_shortage: number;
    quality_issues: number;
  }>;
  loading?: boolean;
}

const PlantRiskAssessmentChart: React.FC<PlantRiskAssessmentChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Risk Assessment</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Risk Assessment</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No risk assessment data available
        </div>
      </div>
    );
  }

  const getRiskLevel = (score: number) => {
    if (score >= 8) return { level: 'Critical', color: '#ef4444' };
    if (score >= 6) return { level: 'High', color: '#f59e0b' };
    if (score >= 4) return { level: 'Medium', color: '#3b82f6' };
    return { level: 'Low', color: '#10b981' };
  };

  const getPlantColor = (index: number) => {
    const colors = [
      '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
      '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
    ];
    return colors[index % colors.length];
  };

  // Transform data for radar chart
  const radarData = [
    { metric: 'Overdue Workshops', ...Object.fromEntries(data.map(item => [item.plant_name, item.overdue_workshops])) },
    { metric: 'Low Progress', ...Object.fromEntries(data.map(item => [item.plant_name, item.low_progress])) },
    { metric: 'High Risk', ...Object.fromEntries(data.map(item => [item.plant_name, item.high_risk])) },
    { metric: 'Delayed Deadlines', ...Object.fromEntries(data.map(item => [item.plant_name, item.delayed_deadlines])) },
    { metric: 'Resource Shortage', ...Object.fromEntries(data.map(item => [item.plant_name, item.resource_shortage])) },
    { metric: 'Quality Issues', ...Object.fromEntries(data.map(item => [item.plant_name, item.quality_issues])) },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Risk Assessment</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <PolarGrid />
            <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
            <PolarRadiusAxis tick={{ fontSize: 10 }} domain={[0, 10]} />
            <Tooltip 
              formatter={(value: number, name: string) => [`${value}`, name]}
              labelFormatter={(label) => `Risk Factor: ${label}`}
            />
            {data.map((plant, index) => (
              <Radar
                key={plant.plant_name}
                name={plant.plant_name}
                dataKey={plant.plant_name}
                stroke={getPlantColor(index)}
                fill={getPlantColor(index)}
                fillOpacity={0.1}
                strokeWidth={2}
              />
            ))}
            <Legend />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Risk Summary</h4>
          <div className="space-y-2">
            {data.map((item, index) => {
              const totalRisk = item.overdue_workshops + item.low_progress + item.high_risk + 
                              item.delayed_deadlines + item.resource_shortage + item.quality_issues;
              const avgRisk = totalRisk / 6;
              const risk = getRiskLevel(avgRisk);
              return (
                <div key={item.plant_name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{ backgroundColor: getPlantColor(index) }}
                    ></div>
                    <span className="text-sm font-medium">{item.plant_name}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{avgRisk.toFixed(1)}</span>
                    <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                      backgroundColor: risk.color + '20', 
                      color: risk.color 
                    }}>
                      {risk.level}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Risk Insights</h4>
          <div className="space-y-3">
            {data.map((item) => {
              const totalRisk = item.overdue_workshops + item.low_progress + item.high_risk + 
                              item.delayed_deadlines + item.resource_shortage + item.quality_issues;
              const avgRisk = totalRisk / 6;
              const risk = getRiskLevel(avgRisk);
              
              return (
                <div key={item.plant_name} className={`p-3 rounded-lg ${risk.level === 'Critical' ? 'bg-red-50' : risk.level === 'High' ? 'bg-orange-50' : risk.level === 'Medium' ? 'bg-blue-50' : 'bg-green-50'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${risk.level === 'Critical' ? 'text-red-600' : risk.level === 'High' ? 'text-orange-600' : risk.level === 'Medium' ? 'text-blue-600' : 'text-green-600'}`}>
                      {item.plant_name}
                    </span>
                    <span className={`font-bold ${risk.level === 'Critical' ? 'text-red-600' : risk.level === 'High' ? 'text-orange-600' : risk.level === 'Medium' ? 'text-blue-600' : 'text-green-600'}`}>
                      {avgRisk.toFixed(1)}
                    </span>
                  </div>
                  <div className={`text-xs ${risk.level === 'Critical' ? 'text-red-500' : risk.level === 'High' ? 'text-orange-500' : risk.level === 'Medium' ? 'text-blue-500' : 'text-green-500'} mt-1`}>
                    {risk.level} Risk Level
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantRiskAssessmentChart;


